import { event, config } from 'vue-gtag'
import type { AnalyticsEvent } from '@/constants/analytics'

/**
 * Track page views manually (useful for SPAs)
 */
export function trackPageView(pageTitle?: string, pagePath?: string) {
    if (!import.meta.env.VITE_GA_MEASUREMENT_ID) {
        console.log('Analytics Page View (Dev Mode):', pageTitle, pagePath)
        return
    }

    config({
        page_title: pageTitle,
        page_location: pagePath || window.location.href,
    })
}

/**
 * A safe, short reason for a failed request, for `_fail` events: the server's
 * error code when it sends one (for example `insufficient_credits`), else the
 * HTTP status, else `unknown`. The message text is never used, because it can
 * hold the user's own input.
 */
export function errorSignal(error: unknown): string {
    const e = error as { response?: { status?: number; data?: { code?: unknown } } } | null
    const code = e?.response?.data?.code
    return String(typeof code === 'string' && code ? code : e?.response?.status ?? 'unknown')
}

/**
 * Track one event name per hub, for components that both hubs share.
 * The hub comes from the URL, as NotificationBell does: /talent/... is the
 * Talent Hub and everything else is the Care Hub.
 * @param events - The event name for each hub
 * @param parameters - Optional event parameters
 */
export function trackHubEvent(
    events: { care: AnalyticsEvent; talent: AnalyticsEvent },
    parameters?: Record<string, any>,
) {
    const isTalent = window.location.pathname.startsWith('/talent')
    trackEvent(isTalent ? events.talent : events.care, parameters)
}

/**
 * Track custom events
 * @param eventName - Use constants from AnalyticsEvents for consistency
 * @param parameters - Optional event parameters
 */
export function trackEvent(eventName: AnalyticsEvent | string, parameters?: Record<string, any>) {
    if (!import.meta.env.VITE_GA_MEASUREMENT_ID) {
        console.log('Analytics Event (Dev Mode):', eventName, parameters)
        return
    }

    event(eventName, parameters || {})
}